import React, { Fragment } from 'react';

import type { ICashbackLevel } from '../../../../../../types/campaign.types';
import { REBATE_CAMPAIGN_TYPE } from '../../../../../../types/campaign.types';
import {
  getLevelFillPercent,
  isLevelFinished,
  isLevelUnlocked,
  isRebateSource,
} from '../../../../../../utils/variants/rebateHelpers';
import { getSegmentGradientVars } from './RebateProgressBarGradient';
import { useCampaign } from '../../../../../../store/campaignsStore';

import styles from './RebateProgressBar.module.css';
import RebateProgressItem from '../../../RebateProgressItem/RebateProgressItem';
import Image from 'shared/components/Image';

const BAR_ICONS_PATH = '/campaigns/icons/';
const LEVEL_CHECKED_ICON = `${BAR_ICONS_PATH}progressBarChecked`;
const LEVEL_LOCKED_ICON = `${BAR_ICONS_PATH}progressBarLock`;
const LEVEL_UNLOCKED_ICON = `${BAR_ICONS_PATH}progressBarUnlock`;
const STARTING_POINT_ON_ICON = `${BAR_ICONS_PATH}progressBarStartingPointOn`;
const STARTING_POINT_OFF_ICON = `${BAR_ICONS_PATH}progressBarStartingPointOff`;

interface RebateProgressBarProps {
  campaignId: string;
}

type PillStatus = 'finished' | 'unlocked' | 'locked';

const PILL_ICON_PATHS: Record<PillStatus, string> = {
  finished: LEVEL_CHECKED_ICON,
  unlocked: LEVEL_UNLOCKED_ICON,
  locked: LEVEL_LOCKED_ICON,
};

interface PillModel {
  levelNumber: number;
  rebatePercent: number;
  status: PillStatus;
}

const RebateProgressBar: React.FC<RebateProgressBarProps> = ({ campaignId }) => {
  const campaign = useCampaign(campaignId, REBATE_CAMPAIGN_TYPE);
  const rebateCampaign = campaign && isRebateSource(campaign) ? campaign : null;
  const cashbackLevels = rebateCampaign?.cashbackLevels ?? [];
  const userProgress = rebateCampaign?.userProgress ?? null;

  const levelCount = cashbackLevels.length;
  const spent = userProgress?.spentBetAmount ?? null;

  // Finished → 'finished'; unlocked but not finished (in progress, incl. level 1
  // at 0 spend) → 'unlocked'; else 'locked'.
  const getPillStatus = (level: ICashbackLevel): PillStatus => {
    if (isLevelFinished(spent, level)) return 'finished';
    if (isLevelUnlocked(spent, level)) return 'unlocked';
    return 'locked';
  };

  const pills: PillModel[] = cashbackLevels.map((level) => ({
    levelNumber: level.levelNumber,
    rebatePercent: level.rebatePercent,
    status: getPillStatus(level),
  }));

  const firstLevel = cashbackLevels.find(level => level.levelNumber === 1);
  const isFirstLevelUnlocked = !!firstLevel && isLevelUnlocked(spent, firstLevel);

  // Figma structure: N progress items and N+1 pills —
  // [start pill][item 1][pill 1][item 2][pill 2] ... [item N][pill N].
  // The leading pill is a starting point with no level/percent; item i carries
  // level i's own progress toward pill i. All items are equal width.

  // The level in progress (unlocked, not finished) gets its own fixed fill
  // gradient (see RebateProgressBarGradient) and is excluded from the green sweep.
  const inProgressFlags = pills.map((pill) => pill.status === 'unlocked');

  // The green gradient sweeps dark-to-bright across the finished levels only,
  // reaching full brightness at the end of the last finished one (no green at
  // all while level 1 is in progress). Fill fractions are 1 for finished levels
  // and 0 for the in-progress and locked ones; each item's [start, end] range is
  // its share of the total finished fill.
  const fillFractions = cashbackLevels.map((level, index) =>
    inProgressFlags[index] ? 0 : getLevelFillPercent(userProgress, level) / 100,
  );
  const totalFilled = fillFractions.reduce((sum, fraction) => sum + fraction, 0);

  let cumulativeFilled = 0;
  const segmentGradientRanges = fillFractions.map((fraction) => {
    const startFraction = totalFilled === 0 ? 0 : cumulativeFilled / totalFilled;
    const endFraction = totalFilled === 0 ? 0 : (cumulativeFilled + fraction) / totalFilled;
    cumulativeFilled += fraction;
    return { startFraction, endFraction };
  });

  return (
    <div className={styles.rebateProgressBar} data-levels={levelCount}>
      <div className={styles.startPill}>
        <Image
          src={isFirstLevelUnlocked ? STARTING_POINT_ON_ICON : STARTING_POINT_OFF_ICON}
          manualAffix=".svg"
          className={styles.pillIcon}
        />
      </div>

      {cashbackLevels.map((level, index) => (
        <Fragment key={level.levelNumber}>
          <div
            className={styles.segment}
            style={getSegmentGradientVars(
              segmentGradientRanges[index].startFraction,
              segmentGradientRanges[index].endFraction,
              inProgressFlags[index],
            )}
          >
            <RebateProgressItem
              cashbackLevel={level}
              variant='card'
              userProgress={userProgress}
            />
          </div>

          <div className={styles.pill}>
            <Image
              src={PILL_ICON_PATHS[pills[index].status]}
              manualAffix=".svg"
              className={styles.pillIcon}
              data-status={pills[index].status}
            />
            <span className={styles.pillPercent} data-pill-status={pills[index].status}>
              {`${pills[index].rebatePercent.toFixed(1)}%`}
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
};

// fallow-ignore-file
export default RebateProgressBar;
